import {useChatMessage} from '@github-ui/copilot-chat/components/ChatMessageContext'
import {VersionName} from '@github-ui/copilot-chat/components/VersionName'
import {useFilteredCopilotAnnotations} from '@github-ui/copilot-chat/hooks/use-filtered-copilot-annotations'
import {
  type FileReference,
  NullMessageId,
  type SnippetReference,
} from '@github-ui/copilot-chat/utils/copilot-chat-types'
import {copilotFeatureFlags} from '@github-ui/copilot-chat/utils/copilot-feature-flags'
import {docLanguages, getLanguageInfo} from '@github-ui/copilot-chat/utils/language-info'
import {disableStreamingFadeIn} from '@github-ui/copilot-markdown'
import {CodeInsightsDialog} from '@github-ui/copilot-markdown/extensions/code-blocks/CodeInsightsDialog'
import {sendEvent} from '@github-ui/hydro-analytics'
import {CodeIcon, LinkExternalIcon, MarkdownIcon, ShieldIcon} from '@primer/octicons-react'
import {IconButton, Spinner} from '@primer/react'
import {clsx} from 'clsx'
import {type PropsWithChildren, startTransition, useCallback, useEffect, useMemo, useRef, useState} from 'react'

import {useContentPreview} from '../../components/ContentPreview/ContentPreviewContext'
import {formatFilePath, parseGitHubUrl} from '../../utils/content-preview-utils'
import {useContentPreviewBlockContext} from '../ContentPreviewBlockContext'
import {ExpandIcon} from './ExpandIcon'
import styles from './FileBlock.module.css'

export interface FileBlockProps {
  language: string
  /** Filename, with extension. */
  name: string
  /** URL to the file in a repository, if available. */
  url?: string
  /** Full contents of the file. */
  code: string
  line: number
  /** Is there more code than is visible in the preview? */
  isClipped: boolean
  /** Is this block currently streaming? */
  isStreaming: boolean
  /** The index of the file block in the message */
  index: number
  /** The start offset of the file block in the message. */
  startOffset: number
  /** The end offset of the file block in the message. */
  endOffset: number
}

function extractRepoInfo(url?: string): {owner: string; name: string} | null {
  if (!copilotFeatureFlags.fileBlockRefMatching || !url) return null

  // Match URLs like: "https://github.com/owner/repo/tree/..."
  const match = /^https?:\/\/(?:github|.*\.ghe)\.com\/([^/]+)\/([^/]+)/.exec(url)

  if (!match || !match[1] || !match[2]) return null

  const owner = match[1]
  const name = match[2]

  return {
    owner,
    name,
  }
}

// Helper function to strip line number fragments from URLs
function stripLineNumbers(url: string): string {
  return url.replace(/#L\d+(-L\d+)?$/, '')
}

function findFileReference(
  url: string | undefined,
  messageReferences: Array<FileReference | SnippetReference> | undefined,
): FileReference | SnippetReference | undefined {
  if (!copilotFeatureFlags.fileBlockRefMatching || !url || !messageReferences) return undefined

  const urlForMatching = stripLineNumbers(url)
  return messageReferences.find(ref => stripLineNumbers(ref.url) === urlForMatching)
}

export function FileBlock({
  language,
  name,
  url,
  code,
  line,
  children,
  isClipped,
  isStreaming,
  index,
  startOffset,
  endOffset,
}: PropsWithChildren<FileBlockProps>) {
  const {message} = useChatMessage()
  const repoInfoUrl = url ? parseGitHubUrl(url) : null
  const filePath =
    repoInfoUrl?.repoOwner && repoInfoUrl?.repoName
      ? formatFilePath(repoInfoUrl.repoOwner, repoInfoUrl.repoName, repoInfoUrl.filePath)
      : name

  const {updateItem, openItem: openFileBlock, openPreviewPane, versionedItems, getOpenItems} = useContentPreview()
  const {
    messageIndex,
    messageId,
    messageTimestamp,
    autoOpenPreviewPane,
    markdown,
    hasAutoOpenedPreviewPaneRef,
    setHasAutoOpenedPreviewPane,
  } = useContentPreviewBlockContext()

  const {name: languageName} = getLanguageInfo(language)

  // The message ID is not stable because message IDs change when the streaming finishes (insert :wat: face here)
  // so we have to use the index instead, which is stable for now (until we implement conversation branching)
  // This is just a stopgap until we can fix message IDs to be stable throughout the message lifecycle
  const baseId = `file:${name}` as const
  const id = `${baseId}#${messageIndex}.${line}` as const

  // If no code yet received, the name might still be streaming. This would add multiple instances of this file since
  // they are uniquely identified by name
  const readyToPreview = !!code

  const versions = versionedItems.get(baseId) ?? []
  const versionIndex = versions.indexOf(id)
  const version = versions.length > 1 && versionIndex !== -1 ? versionIndex + 1 : null

  // Create a ref for the container that opens the preview
  const fileBlockButtonRef = useRef<HTMLDivElement>(null)

  const fileReference = useMemo(() => {
    const refs = message.references?.filter(
      (ref): ref is FileReference | SnippetReference =>
        ref.type === 'file' || ref.type === 'file-v2' || ref.type === 'snippet',
    )
    return findFileReference(url, refs)
  }, [url, message.references])

  // Helper to ensure the preview item exists with current data
  const ensurePreviewItem = useCallback(() => {
    // Defensive check - should not create items without code
    if (!code) return

    if (copilotFeatureFlags.fileBlockRefMatching) {
      updateItem({
        messageId,
        id,
        language,
        name,
        value: code,
        type: 'file',
        timestamp: new Date(messageTimestamp),
        isStreaming,
        isUserEdited: false,
        reference: (() => {
          if (!fileReference) return undefined
          const repoInfo = extractRepoInfo(fileReference.url)
          return {
            type: 'file',
            url: stripLineNumbers(fileReference.url),
            path: fileReference.path,
            repoID: fileReference.repoID,
            repoOwner: repoInfo?.owner ?? fileReference.repoOwner ?? '',
            repoName: repoInfo?.name ?? fileReference.repoName ?? '',
            ref: fileReference.ref,
            commitOID: fileReference.commitOID,
          }
        })(),
      })
    } else {
      updateItem({
        messageId,
        id,
        language,
        name,
        value: code,
        type: 'file',
        timestamp: new Date(messageTimestamp),
        isStreaming,
        isUserEdited: false,
      })
    }
  }, [messageId, id, language, name, code, messageTimestamp, isStreaming, fileReference, updateItem])

  const openItem = useCallback(
    (openPane: boolean, userInitiated: boolean = true) => {
      // When user clicks, ensure the item is created/updated even if the automatic
      // effect didn't run due to validation failure.
      // This prevents blank preview panes when clicking file blocks.
      if (userInitiated && readyToPreview && messageId !== NullMessageId) {
        ensurePreviewItem()
      }

      sendEvent('dotcom_chat.activate', {
        target: 'BROWSER_FILE_OPENED',
        mode: 'immersive',
        ...(copilotFeatureFlags.fileBlockRefMatching ? {isRepoFile: !!fileReference} : {}),
      })
      openFileBlock(id, userInitiated || getOpenItems().length < 1 || index === 0)

      if (openPane) openPreviewPane(fileBlockButtonRef.current)
    },
    [
      openFileBlock,
      id,
      getOpenItems,
      index,
      openPreviewPane,
      fileReference,
      readyToPreview,
      messageId,
      ensurePreviewItem,
    ],
  )

  useEffect(() => {
    // Use startOffset/endOffset to validate this block is part of the current message.
    // The previous indexOf check would fail for indented blocks (list items) or when
    // the preview snippet differs from the full code, causing blank preview panes.
    const isValidOffset =
      startOffset >= 0 &&
      endOffset >= 0 &&
      startOffset < endOffset &&
      startOffset < markdown.length &&
      endOffset <= markdown.length

    // During streaming, we need to be more lenient with offset validation since the markdown
    // may still be forming. However, we still validate basic offset sanity (non-negative, proper ordering),
    // and ensure that at least the start of the block exists within the current markdown.
    const hasBasicValidOffsets =
      startOffset >= 0 && endOffset >= 0 && startOffset < endOffset && startOffset < markdown.length
    const shouldUpdate =
      readyToPreview && messageId !== NullMessageId && (isValidOffset || (isStreaming && hasBasicValidOffsets))

    if (!shouldUpdate) return

    ensurePreviewItem()

    // don't auto-open here or the user wouldn't be able to close while streaming
  }, [readyToPreview, messageId, markdown, startOffset, endOffset, ensurePreviewItem, isStreaming])

  // automatically open the pane only when this block starts streaming.
  // messageId may be NullMessageId if switching to a new subtread, and this fileblock existed in the old subthread.
  useEffect(() => {
    if (isStreaming && readyToPreview && messageId !== NullMessageId) {
      openItem(autoOpenPreviewPane && !hasAutoOpenedPreviewPaneRef.current, false)
      // only auto open the pane once per message.
      setHasAutoOpenedPreviewPane(true)
    }
  }, [
    isStreaming,
    readyToPreview,
    openItem,
    autoOpenPreviewPane,
    messageId,
    hasAutoOpenedPreviewPaneRef,
    setHasAutoOpenedPreviewPane,
  ])

  const [isDialogOpen, setIsDialogOpen] = useState(false)

  const {publicCodeReferences, codeVulnerabilities} = useFilteredCopilotAnnotations(
    message.copilotAnnotations,
    startOffset,
    endOffset,
  )

  return (
    <>
      <div className="position-relative">
        <div
          ref={fileBlockButtonRef}
          className={styles.container}
          onClick={() => {
            if (copilotFeatureFlags.fileBlockTransitionOpen) {
              startTransition(() => openItem(true))
            } else {
              openItem(true)
            }
          }}
          onKeyDown={e => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault()
              if (copilotFeatureFlags.fileBlockTransitionOpen) {
                startTransition(() => openItem(true))
              } else {
                openItem(true)
              }
            }
          }}
          role="button"
          tabIndex={0}
          aria-label={`View file: ${name}`}
          data-testid={`chat-message-view-file-${name}`}
        >
          <div className={styles.header}>
            <div className={styles.language}>
              {/* The spinner's spinning is a CSS animation, so we have to disable the fade-in because it overrides the
          spin. We also disable it for the other icons so it doesn't look wierd when the spinner gets swapped out. */}
              {isStreaming ? (
                <Spinner size="small" className={disableStreamingFadeIn} />
              ) : docLanguages.has(languageName) ? (
                <MarkdownIcon className={disableStreamingFadeIn} />
              ) : (
                <CodeIcon className={disableStreamingFadeIn} />
              )}
              <span className={styles.name}>{filePath}</span>
              {version !== null && <VersionName version={version} />}
            </div>
            <div className={styles.actionsContainer}>
              {(publicCodeReferences.length > 0 || codeVulnerabilities.length > 0) && (
                <IconButton
                  variant="invisible"
                  size="small"
                  icon={ShieldIcon}
                  aria-label="Code insights"
                  onClick={e => {
                    e.stopPropagation()
                    setIsDialogOpen(true)
                  }}
                  onKeyDown={e => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.stopPropagation()
                      setIsDialogOpen(true)
                    }
                  }}
                />
              )}
              {url && (
                <a
                  className={styles.openInNewTabButton}
                  aria-label="Open file in a new tab"
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-open-in-new-tab
                  onClick={e => e.stopPropagation()}
                  onKeyDown={e => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.stopPropagation()
                    }
                  }}
                >
                  <LinkExternalIcon />
                </a>
              )}
              <div className={styles.expandIconContainer}>
                <ExpandIcon />
              </div>
            </div>
          </div>

          <pre className={clsx(styles.previewCode, isClipped && styles.isClipped)} aria-hidden>
            <code>{children}</code>
          </pre>
        </div>
      </div>
      {isDialogOpen && (
        <CodeInsightsDialog
          publicCodeReferences={publicCodeReferences}
          codeVulnerabilities={codeVulnerabilities}
          onClose={() => setIsDialogOpen(false)}
        />
      )}
    </>
  )
}
